import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { openUploadModal } from 'components/Layouts/Form/FormBuilder/Content/AddNewContent/uploadModal';
import useUpdatePrimaryPhoto from 'components/Layouts/Form/FormBuilder/useUpdatePrimaryPhoto';
import { defaultValue } from 'components/shared/LandingPage/contexts/LandingPageContext';
import useAssetsPermissions from 'hooks/useAssetsPermissions';
import {
  addLandingBlockInsideAccordionThunk,
  addLandingBlockThunk,
  editLandingBlockThunk,
  editLeftSideSubheadlineThunk,
  editLandingBlockInsideAccordionThunk,
  removeLandingBlockThunk,
  updateLandingContentThunk,
  removeLandingBlockInsideAccordionThunk,
} from 'store/page/thunks';
import { accountIdSelector, isOwnerSelector } from 'store/user/selectors';
import axiosApi from 'utils/axios';
import { debounce } from 'utils/debounce';
import { handleNestedAccordion } from 'utils/nestedAccordions';

const PAGE_INDEX = 0;

export function useLandingContext({ landingData, page, links }) {
  const dispatch = useDispatch();
  const isOwner = useSelector(isOwnerSelector);
  const accountId = useSelector(accountIdSelector);

  const onChangePrimaryPhoto = useUpdatePrimaryPhoto();
  const { withAssetSettings } = useAssetsPermissions();

  const onReplaceCover = useCallback(async () => {
    const id = landingData.primaryImageId;
    const selected = [];

    if (id) {
      try {
        const info = (await axiosApi.get(`/acc/${accountId}/assets/${id}`)).data;
        selected.push(info);
      } catch (e) {
        if (e?.errors?.status === 404) {
          window.console.warn("The photo doesn't exist");
        }
      }
    }

    const assets = await openUploadModal(dispatch, {
      selected,
      type: 'media',
      suggested: page,
      isPrimaryPhoto: true,
      isOwner,
      multiple: false,
      withAssetSettings,
      modalTitle: selected.length ? 'Replace Photo' : 'Add Photo',
      defaults: {
        name: page.name,
        tags: page.tags,
      },
    });

    if (assets !== null) {
      onChangePrimaryPhoto({
        asset: assets[0],
        point: assets[0]?.imageCoords,
      });
    }
  }, [accountId, dispatch, landingData, onChangePrimaryPhoto]);

  const onUpdateBlock = useCallback(
    (blockIndex) => (newObject) => {
      dispatch(
        editLandingBlockThunk({
          pageIndex: PAGE_INDEX,
          blockIndex,
          newObject,
        }),
      );
    },
    // eslint-disable-next-line
    [],
  );

  const onAddBlock = useCallback(
    (blockIndex) => (newObject) => {
      dispatch(
        addLandingBlockThunk({
          pageIndex: PAGE_INDEX,
          blockIndex,
          newObject,
        }),
      );
    },
    // eslint-disable-next-line
    [],
  );

  const onDeleteBlock = useCallback((blockIndex) => {
    return () => {
      dispatch(
        removeLandingBlockThunk({
          pageIndex: PAGE_INDEX,
          blockIndex,
        }),
      );
    };
    // eslint-disable-next-line
  }, []);

  const onChangePageTitle = useCallback(
    (title) => {
      dispatch(
        updateLandingContentThunk({
          pages: [
            {
              ...landingData,
              title,
            },
          ],
        }),
      );
    },
    [dispatch, landingData],
  );

  const onChangeLeftSideText = useCallback(
    debounce(({ subheadline }) => {
      dispatch(editLeftSideSubheadlineThunk({ subheadline }));
    }, 300),
    [],
  );

  const onAddBlockInsideSpoiler = useCallback(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex, insertPosition) =>
      (newObject) => {
        const handledData = handleNestedAccordion(newObject);
        if (!handledData) return;

        dispatch(
          addLandingBlockInsideAccordionThunk({
            pageIndex: PAGE_INDEX,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
            newObject: handledData,
            insertPosition,
          }),
        );
      },
    [],
  );

  const onUpdateBlockInsideSpoiler = useCallback(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex) =>
      (newObject) => {
        dispatch(
          editLandingBlockInsideAccordionThunk({
            pageIndex: PAGE_INDEX,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
            newObject,
          }),
        );
      },
    [],
  );

  const onDeleteBlockInsideSpoiler = useCallback(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex) =>
      () =>
        dispatch(
          removeLandingBlockInsideAccordionThunk({
            pageIndex: PAGE_INDEX,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
          }),
        ),
    [],
  );

  return {
    ...defaultValue,
    headerOptions: {
      ...defaultValue.headerOptions,
      logoSrc: page.brandSettings?.logo?.url,
      companyName: page.brandSettings?.companyName,
    },
    isInsideBuilder: true,
    isInsidePageBuilder: true,
    landingData,
    page,
    links,
    readOnly: false,
    hasHeader: !isOwner,
    onChangePageTitle,
    onChangeLeftSideText,
    onReplaceCover,
    onUpdateBlock,
    onAddBlock,
    onAddBlockInsideSpoiler,
    onUpdateBlockInsideSpoiler,
    onDeleteBlockInsideSpoiler,
    onDeleteBlock,
  };
}
